import { Injectable, inject } from '@angular/core';
import { Page, TslApiService, TslSessionService } from 'projects/aztalena-core/src/public-api';
import { BehaviorSubject, Observable } from 'rxjs';
import { Topic } from '../forum/topic';
import { Gift } from '../gift/gift';
import { UserInfo } from '../user-info';

export interface AvailabilityInfo {
  available: boolean;
  usernameAvailable: boolean;
  emailAvailable: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class UserService {
  private api = inject(TslApiService);
  private session = inject(TslSessionService);

  private readonly currentUserRelay = new BehaviorSubject<UserInfo>(undefined);

  constructor() {
    this.session.isSignedInObservable.subscribe((isSignedIn) => {
      if (isSignedIn) {
        this.updateCurrentUser();
      } else {
        this.currentUserRelay.next(undefined);
      }
    });
  }

  check(username: string, email: string): Observable<AvailabilityInfo> {
    return this.api.post('user/check', { username, email });
  }

  register(username: string, email: string, password: string): Observable<void> {
    return this.api.post('user/register', { username, email, password });
  }

  verify(token: string, username: string): Observable<void> {
    return this.api.get('user/verify', { token, username });
  }

  forgetPassword(email: string): Observable<void> {
    return this.api.post('user/forget-password', { email });
  }

  resetPassword(username: string, token: string, password: string): Observable<void> {
    return this.api.post('user/reset-password', { username, token, password });
  }

  setupUser(characterId: string, name: string): Observable<UserInfo> {
    return this.api.post('user/setup', { characterId, name });
  }

  getProfile(username: string = ''): Observable<UserInfo> {
    return this.api.get('user/profile', { username });
  }

  updateCurrentUser(): void {
    this.getProfile().subscribe((user) => {
      this.currentUserRelay.next(user);
    });
  }

  get userInfo(): UserInfo {
    return this.currentUserRelay.getValue();
  }

  getCurrentUser(): Observable<UserInfo> {
    return this.currentUserRelay.asObservable();
  }

  getTopics(
    username: string,
    pageNumber: number = 0,
    pageSize: number = 10
  ): Observable<Page<Topic>> {
    return this.api.get('user/topics', { username, pageNumber, pageSize });
  }

  changePassword(currentPwd: string, newPwd: string): Observable<void> {
    return this.api.post('user/change-password', {
      currentPassword: currentPwd,
      newPassword: newPwd
    });
  }

  editProfile(name: string): Observable<void> {
    return this.api.post('user/edit-profile', { name });
  }

  selectAvatarPart(partIds: string[]): Observable<void> {
    return this.api.post('user/avatar', { parts: partIds });
  }

  resendVerificationEmail(username: string): Observable<void> {
    return this.api.post('user/verify/resend-email', { username });
  }

  getGifts(username: string): Observable<Gift[]> {
    return this.api.get('user/gift', { username });
  }

  sendGift(giftId: string, receiverId: string): Observable<void> {
    return this.api.post('user/send-gift', { giftId, receiverId });
  }
}
